import { SourceType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionButton,
  Dialog,
  DialogCloseX,
  DialogContent,
  DialogHeading,
  DialogBody,
  DialogButton,
  DialogButtons,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import {
  useState,
  useCallback,
  useMemo,
} from "react"
import type { ReactNode } from "react"
import styles from "./DataSource.module.scss"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import {
  useCreateDexcomSource,
  useCreateTandemSource,
} from "src/hooks"
import {
  AddSourceErrorToast,
  AddSourceSuccessToast,
} from "src/toasts"
import type { RoutePath } from "src/types"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

const polyglotPrefix = "pages.settings.data_source.connect_to_source_dialog."

interface Props {
  children: ReactNode;
}

export function SourceConnectDialog(props: Props) {
  const navigate = useNavigate()
  const currentPath = useRouterState({
    // note use .href if you want to include query params
    select: (s) => s.resolvedLocation.pathname,
  }) as RoutePath

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const createDexcomSource = useCreateDexcomSource()
  const createTandemSource = useCreateTandemSource()

  const analyticsRef = useSegment()

  const {
    code, iss, scope,
  } = useMemo(
    () => {
      const urlParams = new URLSearchParams(window.location.search)
      return {
        code: urlParams.get("code"),
        iss: urlParams.get("iss"),
        scope: urlParams.get("scope"),
      }
    },
    [],
  )

  const [
    isOpen,
    setIsOpen,
  ] = useState<boolean>(!!code)

  const clearParams = useCallback(
    () => {
    // clear the code from the url params
      void navigate({
        to: currentPath,
        replace: true,
        params: {},
        search: {},
      })
    },
    [
      navigate,
      currentPath,
    ],
  )

  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        clearParams()
      }

      setIsOpen(newOpen)
    },
    [
      setIsOpen,
      clearParams,
    ],
  )

  /*
   * when adding a Tandem source, the "iss" (issuer) param is provided
   * https://datatracker.ietf.org/doc/html/rfc9207#name-response-parameter-iss
   *
   * note: dexcom does not provide the issuer field
   */
  const sourceType = useMemo(
    () => {
      if (!iss) {
        return "dexcom"
      }

      const issuerURL = new URL(iss)
      const hostname = issuerURL.hostname.toLowerCase()
      const tandemDomain = "tandemdiabetes.com"
      const isTandem =
        tandemDomain === hostname || hostname.endsWith(`.${tandemDomain}`)
      return isTandem ? "tandem" : "dexcom"
    },
    [iss],
  )

  const onClick = useCallback(
    () => {
      if (!code) {
        return
      }

      if (sourceType === "tandem") {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            if (!createTandemSource) {
              throw new Error("unable to add source")
            }

            await createTandemSource({
              code,
              iss: iss ?? undefined,
              scope: scope ?? undefined,
            })

            void analyticsRef.current.track(
              "Data Source Added",
              {
                "Data Source": SourceType.Tandem,
              },
            )

            showToast(
              "add-tandem-source-success-toast",
              AddSourceSuccessToast,
            )

            onOpenChange(false)
          } catch (e) {
            const sugarmateError = getSugarmateError(e)

            const ErrorToast = injectProps(
              AddSourceErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )

            showToast(
              "add-tandem-source-error-toast",
              ErrorToast,
            )
          }

          setIsOverlayLoaderVisible(false)
        })()
      }

      if (sourceType === "dexcom") {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            if (!createDexcomSource) {
              throw new Error("unable to add source")
            }

            await createDexcomSource(code)

            void analyticsRef.current.track(
              "Data Source Added",
              {
                "Data Source": SourceType.Dexcom,
              },
            )

            showToast(
              "add-dexcom-source-success-toast",
              AddSourceSuccessToast,
            )

            onOpenChange(false)
          } catch (e) {
            const sugarmateError = getSugarmateError(e)

            const ErrorToast = injectProps(
              AddSourceErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )

            showToast(
              "add-dexcom-source-error-toast",
              ErrorToast,
            )
          }

          setIsOverlayLoaderVisible(false)
        })()
      }
    },
    [
      code,
      iss,
      scope,
      sourceType,
      createDexcomSource,
      createTandemSource,
      onOpenChange,
      showToast,
      analyticsRef,
      setIsOverlayLoaderVisible,
    ],
  )

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      {props.children}
      <DialogContent>
        <DialogHeading>
          {polyglot.t(`${polyglotPrefix}${sourceType}_heading`)}
          <DialogCloseX />
        </DialogHeading>
        <DialogBody>
          {polyglot.t(`${polyglotPrefix}${sourceType}_message`)}
        </DialogBody>
        <DialogButtons>
          <DialogButton stylePreset="secondary">
            {polyglot.t(`${polyglotPrefix}cancel_cta`)}
          </DialogButton>
          <ActionButton
            onClick={onClick}
            stylePreset="primary"
            className={styles.dialogMultiButton}
            id={"confirm-add-dexcom-button"}
          >
            {polyglot.t(`${polyglotPrefix}confirm_cta`)}
          </ActionButton>
        </DialogButtons>
      </DialogContent>
    </Dialog>
  )
}
